import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';

interface ResetFormValues {
  password: string;
  confirmPassword: string;
}

const ResetPassword: React.FC = () => {
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetFormValues>();

  const onSubmit = async (data: ResetFormValues) => {
    try {
      setError('');
      setLoading(true);
      await api.post('/auth/reset-password', { token, password: data.password });
      setDone(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { borderWidth: '1.5px', borderColor: '#BFDBFE', borderRadius: '12px' } as const;

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden gradient-login">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-sky-200 rounded-full mix-blend-multiply filter blur-3xl opacity-25 animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="max-w-md w-full mx-4 relative z-10">
        <h2 className="text-2xl font-bold text-center text-primary-800 mb-1" style={{ letterSpacing: '-0.02em' }}>
          Set a new password
        </h2>
        <p className="text-center text-primary-700 text-base font-medium opacity-80 mb-7">
          Choose a strong password for your account
        </p>

        <div className="bg-white rounded-3xl p-6 border shadow-blue-lg" style={{ borderColor: 'rgba(191, 219, 254, 0.5)' }}>
          {done ? (
            <div className="text-center space-y-4">
              <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800">
                Your password has been reset. You can now sign in with your new password.
              </div>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="w-full h-[52px] gradient-button text-white font-semibold"
                style={{ borderRadius: '14px' }}
              >
                Go to sign in
              </button>
            </div>
          ) : !token ? (
            <div className="text-center space-y-4">
              <div className="rounded-md bg-danger-bg border border-danger-light p-3 text-sm text-danger-dark">
                This reset link is invalid or incomplete. Please request a new one.
              </div>
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="w-full h-[52px] gradient-button text-white font-semibold"
                style={{ borderRadius: '14px' }}
              >
                Request new link
              </button>
            </div>
          ) : (
            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              {error && (
                <div className="rounded-md bg-danger-bg border border-danger-light p-3">
                  <div className="text-sm text-danger-dark font-medium">{error}</div>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1.5">New password</label>
                <input
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 6, message: 'Password must be at least 6 characters' },
                  })}
                  type="password"
                  className="w-full px-3 h-[52px] bg-white/60 text-base text-gray-900 placeholder-gray-400 focus:outline-none"
                  style={inputStyle}
                  placeholder="New password"
                />
                {errors.password && <p className="mt-1.5 text-sm text-danger-dark font-medium">{errors.password.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1.5">Confirm new password</label>
                <input
                  {...register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: (value) => value === watch('password') || 'Passwords do not match',
                  })}
                  type="password"
                  className="w-full px-3 h-[52px] bg-white/60 text-base text-gray-900 placeholder-gray-400 focus:outline-none"
                  style={inputStyle}
                  placeholder="Re-enter new password"
                />
                {errors.confirmPassword && <p className="mt-1.5 text-sm text-danger-dark font-medium">{errors.confirmPassword.message}</p>}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-[52px] gradient-button text-white font-semibold text-md disabled:opacity-50"
                style={{ borderRadius: '14px', boxShadow: '0 4px 12px rgba(37, 99, 235, 0.5)' }}
              >
                {loading ? 'Resetting...' : 'Reset password'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
